import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import React, { useEffect, useRef, useState } from "react";
import { Animated, Platform } from "react-native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import styled from "styled-components/native";
import { Spacer, VDBText } from "..";
import { SpaceType } from "../Spacer/types";

type CheckBoxProps = {
  text?: string;
  checkBoxSize?: number;
  checkedFunction?: any;
  disabled?: boolean;
  labelColor?: string;
  styles?: any;
  productType?: string;
  checkboxRadius?: number;
  fillColor?: string;
  unfillColor?: string;
  iconColor?: boolean;
  isChecked?: boolean;
  onPress?: (checked: boolean) => void;
  testID?: string;
  dataTestId?: string;
};

const CheckBox = ({
  checkBoxSize,
  text,
  labelColor,
  styles,
  fillColor,
  unfillColor,
  checkedFunction,
  isChecked,
  productType,
  onPress,
  disabled,
  testID,
  dataTestId,
}: CheckBoxProps) => {
  const [isMarked, setIsMarketed] = useState(isChecked || false);
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const { theme } = useScreenOptionsHooks();
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    setIsMarketed(isChecked || false);
  }, [isChecked]);

  const handlePress = () => {
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 1.2,
        duration: 200,
        useNativeDriver: Platform.OS !== "web",
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 3,
        useNativeDriver: Platform.OS !== "web",
      }),
    ]).start();
    setIsMarketed(!isMarked);
    if (checkedFunction) {
      checkedFunction(!isMarked, productType);
    } else if (onPress) {
      onPress(!isMarked);
    }
  };

  return (
    <Container
      onPress={handlePress}
      onHoverIn={() => !isMarked && setHovered(true)}
      onHoverOut={() => setHovered(false)}
      disabled={disabled}
      {...(testID && { testID, "data-testId": dataTestId })}
    >
      <Checkbox
        isChecked={isMarked}
        size={checkBoxSize ? checkBoxSize : 20}
        fillColor={fillColor ? fillColor : theme["knob-bg-active"]}
        isHovered={hovered}
        hoverColor={theme["knob-bg-hover"]}
        unfillColor={unfillColor ?? theme["knob-bg-static"]}
        style={[
          {
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        {isMarked && (
          <VDBIcon
            name={VDBIcons.CHECK}
            size={17}
            color={isMarked ? theme["knob-cont"] : "transparent"}
            variant={IconsVariant.VDBCustom}
          />
        )}
      </Checkbox>
      {text && (
        <>
          <Spacer size={SpaceType.Small} />
          <VDBText color={labelColor} type={styles}>
            {text}
          </VDBText>
        </>
      )}
    </Container>
  );
};

export default CheckBox;

const Container = styled.Pressable`
  flex-direction: row;
  align-items: center;
`;

const Checkbox = styled(Animated.View)<{
  isChecked: boolean;
  size: number;
  fillColor: string;
  unfillColor: string;
  isHovered: boolean;
  hoverColor: string;
}>`
  width: ${({ size }: { size: number }) => size}px;
  height: ${({ size }: { size: number }) => size}px;
  border-width: 2px;
  border-radius: 6px;
  margin-right: 10px;
  align-items: center;
  justify-content: center;
  background-color: ${({
    isChecked,
    fillColor,
    unfillColor,
    isHovered,
    hoverColor,
  }: {
    isChecked: boolean;
    fillColor: string;
    unfillColor: string;
    isHovered: boolean;
    hoverColor: string;
  }) => (isChecked ? fillColor : isHovered ? hoverColor : unfillColor)};
  border-color: ${({ fillColor }: { fillColor?: string }) => fillColor ?? ""};
`;
